/* CBG storefront guidance. A Shopify checkout validation app is still required
 * for enforcement outside this theme (including the Shop app). */
(function () {
  'use strict';

  function variantQuantity(cart, variantId) {
    return (cart.items || []).reduce(function (total, item) {
      return total + (String(item.variant_id) === String(variantId) ? Number(item.quantity) : 0);
    }, 0);
  }

  function canAdd(cart, variantId, quantity, limit) {
    return Number.isInteger(quantity) && quantity > 0
      && variantQuantity(cart, variantId) + quantity <= limit;
  }

  function remainingForLine(limit, total, currentLine) {
    return Math.max(0, limit - (total - currentLine));
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { variantQuantity: variantQuantity, canAdd: canAdd, remainingForLine: remainingForLine };
  }
  if (typeof document === 'undefined' || window.CBGStorefrontLoaded) return;
  window.CBGStorefrontLoaded = true;

  var selectors = 'quantity-selector[data-cbg-limit]';
  var replayButtons = new WeakSet();
  var replayForms = new WeakSet();
  // The same variant can appear in several forms. Share its lock until
  // the actual cart write finishes, rather than just until a click is replayed.
  var pendingVariants = new Set();
  var cartWriteObservers = new Set();
  var xhrRequests = new WeakMap();
  var latestCart = null;

  function isCartAdd(url, method) {
    if (String(method || 'GET').toUpperCase() !== 'POST') return false;
    return /\/cart\/add(?:\.js|\.json)?\/?$/.test(new URL(url, window.location.href).pathname);
  }

  function variantIds(body) {
    if (body && typeof body.get === 'function') return [String(body.get('id') || '')];
    if (typeof body !== 'string') return [];
    try {
      var data = JSON.parse(body);
      return (data.items || [data]).map(function (item) { return String(item.id || ''); });
    } catch (error) {
      return [new URLSearchParams(body).get('id') || ''];
    }
  }

  function startCartWrite(body) {
    var ids = variantIds(body);
    var observers = [];
    cartWriteObservers.forEach(function (observer) {
      if (!observer.started && ids.includes(observer.variantId)) {
        observer.started = true;
        clearTimeout(observer.timer);
        observers.push(observer);
      }
    });
    return function (ok) { observers.forEach(function (observer) { observer.finish({ sent: true, ok: ok }); }); };
  }

  // Empire's standard forms use XMLHttpRequest through jQuery; its newer
  // product forms use fetch. Observe both without replacing their UI handlers.
  var nativeOpen = window.XMLHttpRequest.prototype.open;
  var nativeSend = window.XMLHttpRequest.prototype.send;
  window.XMLHttpRequest.prototype.open = function (method, url) {
    xhrRequests.set(this, { method: method, url: url });
    return nativeOpen.apply(this, arguments);
  };
  window.XMLHttpRequest.prototype.send = function (body) {
    var request = xhrRequests.get(this);
    if (request && isCartAdd(request.url, request.method)) {
      var finish = startCartWrite(body);
      var xhr = this;
      this.addEventListener('loadend', function () { finish(xhr.status >= 200 && xhr.status < 300); }, { once: true });
      try { return nativeSend.apply(this, arguments); }
      catch (error) { finish(false); throw error; }
    }
    return nativeSend.apply(this, arguments);
  };

  var nativeFetch = window.fetch;
  window.fetch = function (resource, options) {
    var url = typeof resource === 'string' || resource instanceof URL ? String(resource) : resource.url;
    var method = (options && options.method) || resource.method;
    if (!isCartAdd(url, method)) return nativeFetch.apply(this, arguments);
    var finish = startCartWrite(options && options.body);
    try {
      return nativeFetch.apply(this, arguments).then(function (response) {
        finish(response.ok);
        return response;
      }, function (error) { finish(false); throw error; });
    } catch (error) { finish(false); throw error; }
  };

  function replayUntilWritten(variantId, replay) {
    return new Promise(function (resolve, reject) {
      var observer = { variantId: String(variantId), started: false };
      observer.finish = function (result) {
        clearTimeout(observer.timer);
        cartWriteObservers.delete(observer);
        resolve(result);
      };
      // Only time out if no write starts (for example, invalid product options).
      // Once sent, keep the variant locked until that request actually settles.
      observer.timer = setTimeout(function () { observer.finish({ sent: false, ok: false }); }, 10000);
      cartWriteObservers.add(observer);
      try { replay(); }
      catch (error) {
        clearTimeout(observer.timer);
        cartWriteObservers.delete(observer);
        reject(error);
      }
    });
  }

  async function readCart() {
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    var response = await window.fetch(root + 'cart.js', { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart request failed');
    latestCart = await response.json();
    return latestCart;
  }

  function syncProduct(cart, productId) {
    document.querySelectorAll(selectors).forEach(function (wrapper) {
      if (wrapper.dataset.cbgContext !== 'cart' && wrapper.dataset.cbgProductId === String(productId)) {
        wrapper.dataset.cbgCartQuantity = String(variantQuantity(cart, selectedVariant(wrapper)));
        update(wrapper);
      }
    });
  }

  function message(wrapper, text) {
    var note = wrapper.querySelector('[data-cbg-limit-message]');
    if (!note) {
      var purchaseModule = wrapper.closest && wrapper.closest('[data-cbg-purchase-module]');
      note = purchaseModule && purchaseModule.querySelector('[data-cbg-limit-message]');
    }
    if (note && note.textContent !== text) note.textContent = text;
  }

  function selectedVariant(wrapper) {
    if (wrapper.dataset.cbgContext === 'cart') return wrapper.dataset.cbgVariantId;
    var input = wrapper.querySelector('[data-quantity-input]');
    var form = (input && input.form) || (wrapper.closest && wrapper.closest('form'));
    var field = form && form.elements.namedItem('id');
    return field ? String(field.value || '') : wrapper.dataset.cbgVariantId;
  }

  function buttonVariant(button, form) {
    var field = form && form.elements.namedItem('id');
    return String(button.dataset.variantId || (field && field.value) || '');
  }

  function lineMaximum(wrapper) {
    var limit = Number(wrapper.dataset.cbgLimit);
    var total = Number(wrapper.dataset.cbgCartQuantity);
    if (wrapper.dataset.cbgContext !== 'cart') {
      var cart = latestCart;
      if (!cart) {
        try { cart = { items: JSON.parse(wrapper.dataset.cbgCartVariants || '[]') }; }
        catch (error) { cart = { items: [] }; }
      }
      return Math.max(0, limit - variantQuantity(cart, selectedVariant(wrapper)));
    }
    var peers = Array.from(document.querySelectorAll(selectors)).filter(function (peer) {
      return peer.dataset.cbgContext === 'cart' && peer.dataset.cbgVariantId === wrapper.dataset.cbgVariantId;
    });
    // Include any lines not present in the current rendered cart fragment.
    peers.forEach(function (peer) {
      total += Number(peer.querySelector('[data-quantity-input]').value) - Number(peer.dataset.cbgOriginalQuantity);
    });
    return remainingForLine(limit, total, Number(wrapper.querySelector('[data-quantity-input]').value));
  }

  function update(wrapper) {
    var input = wrapper.querySelector('[data-quantity-input]');
    if (!input) return;
    var maximum = lineMaximum(wrapper);
    input.max = String(maximum);
    var over = Number(input.value) > maximum;
    var text = 'Limit ' + wrapper.dataset.cbgLimit + ' of each variant per order.';
    if (maximum === 0) text += ' Your cart already contains the allowed quantity. Reduce it or remove this line.';
    else if (over) text += ' Reduce this quantity to ' + maximum + ' or less.';
    input.setCustomValidity(over ? text : '');
    message(wrapper, text);
  }

  function refresh() { document.querySelectorAll(selectors).forEach(update); }

  function quantityWrapper(form) {
    if (!form) return null;
    var input = form.elements.namedItem('quantity');
    return (input && input.closest && input.closest(selectors)) || form.querySelector(selectors);
  }

  document.addEventListener('click', function (event) {
    var plus = event.target.closest('[data-quantity-plus]');
    var wrapper = plus && plus.closest(selectors);
    if (!wrapper) return;
    var input = wrapper.querySelector('[data-quantity-input]');
    if (Number(input.value) + Number(input.dataset.incrementValue || 1) > lineMaximum(wrapper)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      update(wrapper);
    }
  }, true);

  document.addEventListener('change', function (event) {
    var wrapper = event.target.closest(selectors);
    if (!wrapper || !event.target.matches('[data-quantity-input]')) return;
    var max = lineMaximum(wrapper);
    if (max >= 1 && Number(event.target.value) > max) event.target.value = String(max);
    refresh();
  }, true);

  // Variant controls update the form's hidden ID asynchronously. Refresh on
  // the next turn so changing variant restores that variant's own allowance.
  document.addEventListener('change', function (event) {
    if (!event.target.matches('[data-quantity-input]')) setTimeout(refresh, 0);
  }, true);
  document.addEventListener('variant-change', function () { setTimeout(refresh, 0); }, true);

  async function guard(event, button, form, replay) {
    var limit = Number(button.dataset.cbgLimit);
    if (!limit) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    var productId = button.dataset.cbgProductId;
    var wrapper = quantityWrapper(form);
    var input = wrapper && wrapper.querySelector('[data-quantity-input]');
    var variantId = buttonVariant(button, form);
    if (!variantId) { if (wrapper) message(wrapper, 'Choose a variant first.'); return; }
    if (pendingVariants.has(variantId)) return;
    pendingVariants.add(variantId);
    var quantity = input ? Number(input.value) : 1;
    try {
      var cart = await readCart();
      if (buttonVariant(button, form) !== variantId || (input && Number(input.value) !== quantity)) {
        if (wrapper) message(wrapper, 'Your selection changed. Press Add to cart again.');
        return;
      }
      if (!canAdd(cart, variantId, quantity, limit)) {
        if (wrapper) {
          wrapper.dataset.cbgCartQuantity = String(variantQuantity(cart, variantId));
          update(wrapper);
          if (input) input.reportValidity();
        } else {
          var note = button.parentElement.querySelector('[data-cbg-add-message]');
          if (!note) {
            note = document.createElement('p');
            note.dataset.cbgAddMessage = '';
            note.setAttribute('role', 'status');
            button.parentElement.appendChild(note);
          }
          note.textContent = 'Limit ' + limit + ' of each variant per order. Please review your cart.';
        }
        return;
      }
      if (wrapper) { wrapper.dataset.cbgCartQuantity = String(variantQuantity(cart, variantId)); update(wrapper); }
      var result = await replayUntilWritten(variantId, replay);
      if (result.sent) syncProduct(await readCart(), productId);
      else if (wrapper) message(wrapper, 'Could not start this addition. Check the product options and try again.');
    } catch (error) {
      if (wrapper) message(wrapper, 'Unable to check your cart. Please try again.');
      else window.alert('Unable to check your cart. Please try again.');
    } finally { pendingVariants.delete(variantId); }
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-cbg-add]');
    if (!button || button.disabled || button.hasAttribute('data-quickshop-slim')) return;
    if (replayButtons.has(button)) { replayButtons.delete(button); return; }
    guard(event, button, button.form, function () {
      replayButtons.add(button);
      if (button.form) replayForms.add(button.form);
      button.click();
      setTimeout(function () {
        replayButtons.delete(button);
        if (button.form) replayForms.delete(button.form);
      }, 0);
    });
  }, true);

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (replayForms.has(form)) { replayForms.delete(form); return; }
    var button = form.querySelector('[data-product-atc][data-cbg-add]');
    if (!button || button.disabled) return;
    guard(event, button, form, function () {
      replayForms.add(form);
      form.requestSubmit(button);
      replayForms.delete(form);
    });
  }, true);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh);
  else refresh();
  document.addEventListener('shopify:section:load', refresh);
  new MutationObserver(function (records) {
    if (records.some(function (record) {
      return Array.from(record.addedNodes).some(function (node) {
        return node.nodeType === 1 && (node.matches(selectors) || node.querySelector(selectors));
      });
    })) refresh();
  }).observe(document.documentElement, { childList: true, subtree: true });
}());
